import React, { useState, useEffect, useMemo } from "react";
import "../../styles/AdminStyling/CurrentUsers.css";
import axios from "axios";
import Select from "react-select";
import { SignUpForm } from "./SignUpForm";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

export const CurrentUsers = ({
  onEditUser,
  refreshTrigger,
  activeTab,
  setRefreshUsers,
}) => {
  const AddUser = `${process.env.PUBLIC_URL}/assets/logos/add-user.svg`;
  const DeleteIcon = `${process.env.PUBLIC_URL}/assets/logos/delete-icon.svg`;
  const EditIcon = `${process.env.PUBLIC_URL}/assets/logos/edit-icon.svg`;
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteUser, setDeleteUser] = useState(null);
  const [sortConfig, setSortConfig] = useState(null);
  // Default selected options are empty arrays – meaning no filter (i.e. show all)
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [showSignUpForm, setShowSignUpForm] = useState(false);
  // Pagination state: current page and constant for users per page.
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 25;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/users`
        );
        setUsers(response.data.users || []);
      } catch (err) {
        console.error("Error fetching users!", err);
        setError("Failed to fetch users. Please try again.");
      }
    };

    fetchUsers();
  }, [refreshTrigger]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleEditUser = (userId) => {
    onEditUser && onEditUser(userId);
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    const expectedText = `DELETE ${deleteUser.fname} ${deleteUser.lname}`;
    if (deleteConfirm !== expectedText) {
      setNotification("Confirmation text does not match. Please try again.");
      return;
    }
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/delete_user?userid=${deleteUser.userid}`,
        { withCredentials: true }
      );
      if (response.data.success) {
        setNotification("User deleted successfully.");
        setUsers(users.filter((user) => user.userid !== deleteUser.userid));
        setDeleteUser(null);
        setDeleteConfirm("");
      } else {
        setNotification(response.data.message);
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      setNotification("Failed to delete user. Please try again.");
    }
  };

  const roleOptions = [
    ...new Set(
      users.flatMap((user) =>
        Array.isArray(user.role) ? user.role : [user.role]
      )
    ),
  ];
  const companyOptions = [
    ...new Set(
      users.flatMap((user) =>
        Array.isArray(user.company) ? user.company : [user.company]
      )
    ),
  ];

  // Create react-select options without an "All" option.
  const roleSelectOptions = roleOptions.map((role) => ({
    value: role,
    label: role,
  }));
  const companySelectOptions = companyOptions.map((company) => ({
    value: company,
    label: company,
  }));

  // Filtering logic applied on the entire users array.
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const roleMatch =
        selectedRoles.length === 0
          ? true
          : Array.isArray(user.role)
          ? selectedRoles.some((option) => user.role.includes(option.value))
          : selectedRoles.some((option) => user.role === option.value);
      const companyMatch =
        selectedCompanies.length === 0
          ? true
          : Array.isArray(user.company)
          ? selectedCompanies.some((option) =>
              user.company.includes(option.value)
            )
          : selectedCompanies.some((option) => user.company === option.value);
      return roleMatch && companyMatch;
    });
  }, [users, selectedRoles, selectedCompanies]);

  const columnHeaders = {
    fname: "First Name",
    lname: "Last Name",
    email: "Email",
    role: "Role",
    company: "Sponsor Access",
    isactivated: "Activated",
    lastlogin: "Last Login",
  };

  // Sorting is applied on the entire filtered list.
  const sortedUsers = useMemo(() => {
    if (!filteredUsers || filteredUsers.length === 0) return [];
    if (!sortConfig) return filteredUsers;
    return [...filteredUsers].sort((a, b) => {
      const key = sortConfig.key;
      let aValue = a[key];
      let bValue = b[key];
      if (Array.isArray(aValue)) aValue = aValue.join(", ");
      if (Array.isArray(bValue)) bValue = bValue.join(", ");
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (key === "lastlogin") {
        aValue = aValue ? new Date(aValue).getTime() : Number.MAX_VALUE;
        bValue = bValue ? new Date(bValue).getTime() : Number.MAX_VALUE;
      } else if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredUsers, sortConfig]);

  // Whenever the filters or sorting change, reset the current page to 1.
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedRoles, selectedCompanies, sortConfig]);

  // Pagination: calculate total pages and the current users slice from the sorted list.
  const totalPages = Math.ceil(sortedUsers.length / usersPerPage);
  const currentUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * usersPerPage;
    return sortedUsers.slice(startIndex, startIndex + usersPerPage);
  }, [sortedUsers, currentPage, usersPerPage]);

  const handleSort = (key) => {
    setSortConfig((prevSort) => ({
      key,
      direction:
        prevSort?.key === key && prevSort.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Handlers for react-select – setting selected options directly.
  const handleRoleChange = (selectedOptions) => {
    setSelectedRoles(selectedOptions || []);
  };

  const handleCompanyChange = (selectedOptions) => {
    setSelectedCompanies(selectedOptions || []);
  };

  return (
    <>
      {showSignUpForm && (
        <div className="modal-overlay">
          <SignUpForm
            closeForm={() => setShowSignUpForm(false)}
            onSuccess={() => setRefreshUsers((prev) => prev + 1)}
          />
        </div>
      )}
      <div className="admin-title-container">
        {activeTab === "users" && (
          <button
            className="add-user-button"
            onClick={() => setShowSignUpForm(true)}
          >
            <img
              src={activeTab === "users" ? AddUser : AddUser}
              alt=""
              style={{ marginRight: "10px" }}
            />
            Add User
          </button>
        )}
      </div>
      <div className="filter-container">
        <label>Filter by Role: </label>
        <div
          style={{
            width: "200px",
            display: "inline-block",
            marginRight: "20px",
          }}
        >
          <Select
            isMulti
            options={roleSelectOptions}
            value={selectedRoles}
            onChange={handleRoleChange}
            placeholder="Select roles..."
            isSearchable
            isClearable
          />
        </div>
        <label>Filter by Sponsor: </label>
        <div style={{ width: "200px", display: "inline-block" }}>
          <Select
            isMulti
            options={companySelectOptions}
            value={selectedCompanies}
            onChange={handleCompanyChange}
            placeholder="Select sponsors..."
            isSearchable
            isClearable
          />
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      <div
        className="current-users-container"
        //  style={{ overflowY: "scroll", position: "relative" }}
      >
        <table className="users-table">
          <thead>
            <tr>
              {Object.entries(columnHeaders).map(([key, label]) => (
                <th key={key} onClick={() => handleSort(key)}>
                  {label}
                  <span
                    className={`sort-arrow ${
                      sortConfig?.key === key
                        ? sortConfig.direction === "asc"
                          ? "asc"
                          : "desc"
                        : ""
                    }`}
                  >
                    {sortConfig?.key === key
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </span>
                </th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.length > 0 ? (
              currentUsers.map((user) => (
                <tr key={user.userid}>
                  <td>{user.fname}</td>
                  <td>{user.lname}</td>
                  <td>{user.email}</td>
                  <td>
                    {Array.isArray(user.role)
                      ? user.role.join(", ")
                      : user.role || ""}
                  </td>
                  <td>
                    {Array.isArray(user.company)
                      ? user.company.join(", ")
                      : user.company || ""}
                  </td>
                  <td>{user.isactivated ? "Yes" : "No"}</td>
                  <td>
                    {user.lastlogin
                      ? new Date(user.lastlogin).toLocaleString()
                      : "N/A"}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <img
                        alt="Edit"
                        className="user-edit-btn"
                        onClick={() => handleEditUser(user.userid)}
                        src={EditIcon}
                      />
                      <img
                        alt="Delete"
                        className="user-delete-btn"
                        onClick={() => setDeleteUser(user)}
                        src={DeleteIcon}
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9">No users found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination controls with react-icons and custom styling */}
      {sortedUsers.length > usersPerPage && (
        <div
          className="pagination"
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              background: "none",
              border: "none",
              cursor: currentPage === 1 ? "default" : "pointer",
            }}
          >
            <FaChevronLeft color="black" size={20} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => setCurrentPage(i + 1)}
              style={{
                width: "30px",
                height: "30px",
                lineHeight: "30px",
                border: "none",
                borderRadius: currentPage === i + 1 ? "50%" : "50%",
                backgroundColor: currentPage === i + 1 ? "#007bff" : "#ccc",
                color: currentPage === i + 1 ? "white" : "black",
                cursor: "pointer",
              }}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              background: "none",
              border: "none",
              cursor: currentPage === totalPages ? "default" : "pointer",
            }}
          >
            <FaChevronRight color="black" size={20} />
          </button>
        </div>
      )}
      {deleteUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Deletion</h3>
            <p>
              To confirm deletion, type:{" "}
              <strong>
                DELETE {deleteUser.fname} {deleteUser.lname}
              </strong>
            </p>
            <input
              type="text"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder={`DELETE ${deleteUser.fname} ${deleteUser.lname}`}
            />
            <div className="delete-actions">
              <button className="delete-confirm-btn" onClick={handleDeleteUser}>
                Delete User
              </button>
              <button
                className="cancel-btn"
                onClick={() => {
                  setDeleteUser(null);
                  setDeleteConfirm("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {notification && (
        <div
          className="notification-toast"
          onClick={() => setNotification(null)}
        >
          {notification}
        </div>
      )}
    </>
  );
};

export default CurrentUsers;
